import { useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  Column,
  Divider,
  Flex,
  Heading,
  SettingField,
  Spacer,
  Toggle,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { getCompanyCostSetup, updateCompanyCostSetup } from 'src/store/entities/company-settings/company-settings';
import { selectCompanyCostSetup } from 'src/store/entities/company-settings/selectors';
import translations from 'src/internationalisation/translation-map.json';
import { autoSaveWaitShort } from 'src/config/config';

import type { TRootState } from 'src/store/store-types';

const CostSetup = ({ companyCostSetup, getCompanyCostSetup, updateCompanyCostSetup }: PropsFromRedux) => {
  const { t } = useTranslation();
  const { company } = useParams();

  useEffect(() => {
    getCompanyCostSetup(company);
  }, [company]);

  const debounceUpdatedCompanyCostSetup = useRef(debounce(updateCompanyCostSetup, autoSaveWaitShort));

  const onChangeCostSetupSettings = (changedSettings: { [x: string]: boolean }) => {
    const changes = {
      ...companyCostSetup,
      ...changedSettings,
    };
    debounceUpdatedCompanyCostSetup.current(company, changes);
  };

  return (
    <Column spacing={0}>
      <Spacer height={20} />
      <Flex>
        <Spacer width="var(--padding-sm)" />
        <Heading top>{t(translations.projects_costSetup)}</Heading>
      </Flex>
      <Divider margin={0} />
      <SettingField noPermission>
        <Flex gap={10} alignItems="center">
          {'Expanded volume inputs'}
          <Toggle
            name={'mudVolume'}
            checked={companyCostSetup?.mudVolume}
            onChange={({ target: { checked } }) => {
              onChangeCostSetupSettings({ mudVolume: checked });
            }}
          />
        </Flex>
      </SettingField>
    </Column>
  );
};

const mapStateToProps = (state: TRootState) => ({
  companyCostSetup: selectCompanyCostSetup(state.entities),
});

const mapDispatchToProps = {
  getCompanyCostSetup,
  updateCompanyCostSetup,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(CostSetup));

export { Container as CostSetup };
